import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, BookOpen, FileText, ClipboardList, TrendingUp, Clock } from 'lucide-react';

const Dashboard = ({ onNavigate }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get('/admin/dashboard-stats');
      setStats(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  const statCards = [
    {
      title: 'Total Users',
      value: stats?.total_users || 0,
      icon: Users,
      color: 'bg-blue-500',
    },
    {
      title: 'Total Courses',
      value: stats?.total_courses || 0,
      icon: BookOpen,
      color: 'bg-green-500',
    },
    {
      title: 'Mock Tests',
      value: stats?.total_tests || 0,
      icon: FileText,
      color: 'bg-blue-600',
    },
    {
      title: 'Questions',
      value: stats?.total_questions || 0,
      icon: ClipboardList,
      color: 'bg-orange-500',
    },
    {
      title: 'Enrollments',
      value: stats?.total_enrollments || 0,
      icon: TrendingUp,
      color: 'bg-pink-500',
    },
    {
      title: 'Test Attempts',
      value: stats?.total_attempts || 0,
      icon: Clock,
      color: 'bg-blue-500',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="mb-4">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">Dashboard</h2>
        <p className="text-gray-600 text-sm sm:text-base">Overview of your platform activity</p>
      </div>

      {/* Stats Grid - 2x2 layout on mobile, 4 columns on desktop */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="hover:shadow-lg transition-all hover:scale-105 border-l-4" style={{borderLeftColor: stat.color.replace('bg-', '').includes('blue') ? '#3B82F6' : stat.color.replace('bg-', '').includes('green') ? '#10B981' : stat.color.replace('bg-', '').includes('orange') ? '#F59E0B' : stat.color.replace('bg-', '').includes('pink') ? '#EC4899' : '#3B82F6'}}>
              <CardContent className="pt-4 pb-4 px-3 sm:px-4">
                <div className="flex flex-col items-center text-center space-y-2">
                  <div className={`${stat.color} p-2 sm:p-3 rounded-full`}>
                    <Icon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl sm:text-3xl font-bold text-gray-900">{stat.value}</p>
                    <p className="text-xs sm:text-sm font-medium text-gray-600 mt-1">{stat.title}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
        <CardContent className="pt-4 pb-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <button 
              onClick={() => onNavigate('courses')}
              className="flex flex-col items-center p-3 rounded-lg bg-white shadow-sm hover:shadow-md transition-all hover:scale-105 border border-blue-200"
            >
              <BookOpen className="w-5 h-5 text-blue-600 mb-1" />
              <span className="text-xs font-medium text-gray-700">Add Course</span>
            </button>
            <button 
              onClick={() => onNavigate('tests')}
              className="flex flex-col items-center p-3 rounded-lg bg-white shadow-sm hover:shadow-md transition-all hover:scale-105 border border-green-200"
            >
              <FileText className="w-5 h-5 text-green-600 mb-1" />
              <span className="text-xs font-medium text-gray-700">Add Test</span>
            </button>
            <button 
              onClick={() => onNavigate('users')}
              className="flex flex-col items-center p-3 rounded-lg bg-white shadow-sm hover:shadow-md transition-all hover:scale-105 border border-orange-200"
            >
              <Users className="w-5 h-5 text-orange-600 mb-1" />
              <span className="text-xs font-medium text-gray-700">Manage Users</span>
            </button>
            <button 
              onClick={() => onNavigate('analytics')}
              className="flex flex-col items-center p-3 rounded-lg bg-white shadow-sm hover:shadow-md transition-all hover:scale-105 border border-pink-200"
            >
              <TrendingUp className="w-5 h-5 text-pink-600 mb-1" />
              <span className="text-xs font-medium text-gray-700">Analytics</span>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Test Attempts</CardTitle>
          <CardDescription>Latest test submissions from students</CardDescription>
        </CardHeader>
        <CardContent>
          {stats?.recent_activity && stats.recent_activity.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_activity.map((attempt, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                >
                  <div>
                    <p className="font-medium text-sm">Test ID: {attempt.test_id.substring(0, 8)}...</p>
                    <p className="text-xs text-gray-500">
                      {new Date(attempt.completed_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-blue-600">
                      Score: {attempt.score?.toFixed(2)} / {attempt.total_questions}
                    </p>
                    <p className="text-xs text-gray-500">
                      {((attempt.score / attempt.total_questions) * 100).toFixed(1)}%
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No recent activity</p>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common administrative tasks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button 
              onClick={() => onNavigate && onNavigate('courses')}
              className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-center cursor-pointer"
            >
              <BookOpen className="w-8 h-8 mx-auto mb-2 text-gray-600" />
              <p className="text-sm font-medium">Add Course</p>
            </button>
            <button 
              onClick={() => onNavigate && onNavigate('tests')}
              className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-center cursor-pointer"
            >
              <FileText className="w-8 h-8 mx-auto mb-2 text-gray-600" />
              <p className="text-sm font-medium">Create Test</p>
            </button>
            <button 
              onClick={() => onNavigate && onNavigate('tests')}
              className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-center cursor-pointer"
            >
              <ClipboardList className="w-8 h-8 mx-auto mb-2 text-gray-600" />
              <p className="text-sm font-medium">Add Questions</p>
            </button>
            <button 
              onClick={() => onNavigate && onNavigate('users')}
              className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-center cursor-pointer"
            >
              <Users className="w-8 h-8 mx-auto mb-2 text-gray-600" />
              <p className="text-sm font-medium">Manage Users</p>
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
